import { ReactNode } from 'react';

import { MayHaveLabel } from '@nivo/pie';
import { formatCurrency, formatPercent } from 'configs/columns';
import { PoolSummaryPerformanceSummary } from 'features/loanPool/__generated__/PoolSummaryPerformanceSummary';
import { ChartType_V2 } from 'features/visualizations';
import { invert, mapValues } from 'lodash';
import moment from 'moment';
import { FormatNumberOptions, IntlShape } from 'react-intl';
import AssetClassConfig from 'types/assetClass';

import { AssetClass, FilterableField } from '__generated__/globalTypes';

import { StratificationsPerformanceSummary } from './__generated__/StratificationsPerformanceSummary';

const currentCentsCompact: FormatNumberOptions = {
  maximumFractionDigits: 0,
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
};

type StratValueField = 't_current_balance_cents' | 't_original_balance_cents' | 'loanCount';

type StratConfig = {
  [key in keyof StratificationsPerformanceSummary]?: {
    label: string;
    chart: ChartType_V2;
    filterField: FilterableField;
    valueField?: StratValueField;
    valueFormatOptions?: FormatNumberOptions & {
      prepareNumber?: (value: number) => number;
    };
    yAxisLabel?: string;
    avgField?: keyof PoolSummaryPerformanceSummary;
    avgFormat?: (intl: IntlShape, value: number) => string;
    avgLabel?: string;
    abbr: string;
  };
};

export const stratificationsConfig: StratConfig = {
  strat_age_months: {
    label: 'Loan Age',
    chart: 'Pie',
    filterField: FilterableField.age_months,
    avgField: 'averageAge',
    abbr: 'LA',
  },
  strat_borrower_credit_score: {
    label: 'WA FICO',
    chart: 'Bar_Vertical_Dots',
    filterField: FilterableField.borrower_credit_score,
    valueField: 't_current_balance_cents',
    valueFormatOptions: {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
      prepareNumber: (num: number) => num / 100,
    },
    yAxisLabel: 'UPB',
    abbr: 'BCS',
  },
  strat_current_balance_cents: {
    label: 'UPB',
    chart: 'Pie',
    filterField: FilterableField.current_balance_cents,
    avgField: 'averageBalance',
    avgFormat: (intl, value) => intl.formatNumber(value / 100, currentCentsCompact),
    abbr: 'UPB',
  },
  strat_original_balance_cents: {
    label: 'Original Balance',
    chart: 'Pie',
    filterField: FilterableField.original_balance_cents,
    avgField: 'averageOriginalBalance',
    avgFormat: (intl, value) => intl.formatNumber(value / 100, currentCentsCompact),
    abbr: 'OB',
  },
  strat_dti: {
    label: 'DTI',
    chart: 'Bar_Horizontal_Dots',
    filterField: FilterableField.dti_bps,
    valueField: 'loanCount',
    valueFormatOptions: {
      maximumFractionDigits: 0,
    },
    yAxisLabel: 'Loan Count',
    abbr: 'DTI',
  },
  strat_interest_rate: {
    label: 'Interest Rate',
    chart: 'Bar_Vertical_Dots',
    filterField: FilterableField.interest_rate_bps,
    abbr: 'IR',
  },
  strat_ltv: {
    label: 'LTV',
    chart: 'Bar_Vertical',
    filterField: FilterableField.ltv_bps,
    abbr: 'LTV',
  },
  strat_first_payment_date: {
    label: 'First Payment Date',
    chart: 'Bar_Horizontal',
    filterField: FilterableField.first_payment_date_seconds,
    abbr: 'OD',
  },
  strat_occupancy: {
    label: 'Occupancy',
    chart: 'Pie',
    filterField: FilterableField.occupancy,
    avgField: 'loanCount',
    avgLabel: 'loans',
    abbr: 'OCC',
  },
  strat_product: {
    label: 'Product',
    chart: 'Pie',
    filterField: FilterableField.product,
    avgField: 'loanCount',
    avgLabel: 'loans',
    abbr: 'PD',
  },
  strat_purpose: {
    label: 'Purpose',
    chart: 'Pie',
    filterField: FilterableField.purpose,
    avgField: 'loanCount',
    avgLabel: 'loans',
    abbr: 'PUR',
  },
  strat_remaining_loan_terms_months: {
    label: 'Maturity Date',
    chart: 'Bar_Vertical_Dots',
    filterField: FilterableField.maturity_date_seconds,
    abbr: 'MD',
  },
  strat_state: {
    label: 'State',
    chart: 'States',
    filterField: FilterableField.state,
    abbr: 'ST',
  },
};

export type StratKey = keyof typeof stratificationsConfig;

export type StratRowType = {
  [K in StratKey]: StratificationsPerformanceSummary[K] extends readonly (infer T)[]
    ? T & MayHaveLabel
    : never;
}[StratKey];

type StratColConfig<T extends StratRowType, K extends keyof T> = {
  header: string;
  // sortSelector?: SortableField;
  display: (value: T[K]) => string | ReactNode;
};
export const stratsColumnsConfig = {
  t_current_balance_cents: {
    header: 'Total Current Balance',
    display: (value: number | null) => formatCurrency(value),
  },
  t_original_balance_cents: {
    header: 'Total Original Balance',
    display: (value: number | null) => formatCurrency(value),
  },
  loanCount: {
    header: 'Total Count',
    display: (value: number | null) => formatInterger(value),
  },
  wa_coupon_bps: {
    header: 'GWAC',
    display: (value: number | null) => formatPercent(value, 3),
  },
  wa_net_rate: {
    header: 'NWAC',
    display: (value: number | null) => (value != null ? `${(value * 100).toFixed(3)}%` : '-'),
  },
  wa_borrower_credit_score: {
    header: 'CREDIT SCORE',
    display: (value: number | null) => formatInterger(value),
  },
  wa_remaining_loan_terms_months: {
    header: 'WAM',
    display: (value: number | null) => formatInterger(value),
  },
  wa_age_months: {
    header: 'WALA',
    display: (value: number | null) => formatInterger(value),
  },
} satisfies { [key in keyof StratRowType]?: StratColConfig<StratRowType, key> };

export const abbrToKeyMap = invert(
  mapValues(stratificationsConfig, (v) => v?.abbr) as Record<string, string>,
) as { [key: string]: StratKey };

const formatInterger = (value: number | null) =>
  value != null && isFinite(value) ? value.toLocaleString() : '-';

export const stratsColumnOrder: (keyof typeof stratsColumnsConfig)[] = [
  't_current_balance_cents',
  't_original_balance_cents',
  'loanCount',
  'wa_coupon_bps',
  'wa_net_rate',
  'wa_borrower_credit_score',
  'wa_remaining_loan_terms_months',
  'wa_age_months',
];
